import { createStyles, Theme, makeStyles } from '@material-ui/core';

import React, {
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import { isMobile } from 'react-device-detect';
import Image from '@cube3/cubicle/src/core/atoms/Image/Image';
import DetailviewFullscreenControls from '../DetailviewFullscreenControls';
import { PreviewHOCProps } from '../PreviewWrapper';
import ImagePreviewControls, { ImagePreviewControlsMobile } from './ImagePreviewControls';
import PanZoom from './PanZoom';
import { PanZoomProvider } from './PanZoomContext';
import { Typography } from '@cube3/cubicle/src/core/atoms/Typography/Typography';
import { SizeVariants } from '@cube3/cubicle/src/theme/themes';
import Truncate from '../../human/Truncate';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      flex: '1',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-end',
      height: '100%',
      width: '100%'
    },
    containerMobile: {
      alignSelf: 'center',
      width: '100%',
      height: '100%',
      background: 'black'
    },
    fullscreenMobile: {
      marginTop: 60
    },
    controls: {
      zIndex: 2
    },
    controlsMobile: {
      position: 'absolute',
      bottom: 0,
      right: 0,
      width: '100%'
    },
    viewport: {
      position: 'absolute',
      width: '100%',
      height: '100%',
      top: 0,
      left: 0
    },
    title: {
      height: '20px',
      left: '28px',
      right: '28px',
      top: '21px',
      color: theme.customPalette.primary.contrastColor,
      position: 'absolute',
      zIndex: 1,
      ...theme.typographyStyles.heading2,
      letterSpacing: '1px'
    },
    image: {
      position: 'relative',
      height: '100%'
    },
    layers: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%'
    }
  })
);

interface ImageLayerProps {
  assetId: string;
  src?: string;
  type?: 'video' | 'audio' | 'image';
  mediaElement: HTMLImageElement;
}

interface ImagePreviewProps extends PreviewHOCProps {
  id: string;
  asset_variant?: string;
  name: string;
  display_name: string;
  src: string;
  thumbnail?: string;
  dimensions?: {
    width: number;
    height: number;
  };
  gotoNextSibling?: () => void;
  gotoPrevSibling?: () => void;
  siblings: boolean;
  // activity
  setViewed?: Function;
  /** Copy image to clipboard handler  */
  onCopy?: () => void;
  layers?: React.ComponentType<ImageLayerProps>[] | false;
  drawingBarToggled?: boolean;
  renderSocialMediaMockupLayer?: () => JSX.Element;
  toggleDrawingBar?: () => void;
}

const ImagePreview: React.FC<ImagePreviewProps> = (props) => {
  const mainContainerRef = useRef<HTMLDivElement>();
  const {
    id,
    src,
    thumbnail,
    dimensions,
    toggleFullscreenAction,
    fullscreen,
    gotoNextSibling,
    gotoPrevSibling,
    display_name,
    siblings,
    //
    setViewed,
    onCopy,
    layers,
    drawingBarToggled,
    // 
    renderSocialMediaMockupLayer,
    toggleDrawingBar
  } = props;

  const classes = useStyles();

  // create new activity if the active asset has changed
  // e.g.: go to prev/next sibling, preview assets in review tools
  const createdRef = useRef(false);
  useEffect(() => {
    if (!id) return;
    if (!createdRef.current) {
      setViewed?.(id);
      createdRef.current = true;
    }
  }, [id]);

  const [imageRef, setImageRef] = useState<HTMLImageElement>();
  const handleImageRef = useCallback(
    (node: HTMLImageElement | null) => {
      setImageRef(node);
    },
    [setImageRef]
  );


  return (
    <div className={`${isMobile ? classes.containerMobile : classes.container} ${fullscreen && isMobile ? classes.fullscreenMobile : ''}`} >

      <PanZoomProvider useWheel={isMobile ? false : true} key={fullscreen ? 'fullscreen' : 'regular'}>
        <div className={classes.viewport}>
          {renderSocialMediaMockupLayer && renderSocialMediaMockupLayer()}

          {isMobile && fullscreen && (
            <div className={classes.title}>
              <Typography
                weight="label"
                variant={SizeVariants.lg}
                colorVariant="01"
                color="dark"
              >
                <Truncate str={display_name} />
              </Typography>
            </div>
          )}
          <PanZoom
            offset={{ bottom: '4rem', right: 0, left: 0, top: '0' }}
            data-testid="image-preview"
            layers={
              layers && (
                <div className={`${classes.layers}`} data-set="preventDeselect">
                  {layers.map((Layer, idx) => {
                    return (
                      <Layer
                        key={`layer-${idx}`}
                        assetId={id}
                        mediaElement={imageRef}
                        src={src}
                        type={'image'}
                      />
                    );
                  })}
                </div>
              )
            }
          >

            <div className={classes.image}>
              <Image
                ref={handleImageRef}
                src={src}
                background="checkerboard"
                placeholder={thumbnail}
                dimensions={dimensions}
                fullSize={true}
                floatingEffect={false}
              />
            </div>
          </PanZoom>
        </div>
        {!drawingBarToggled && (
          <div
            className={`${isMobile ? classes.controlsMobile : classes.controls}`}>
            {isMobile ?
              <ImagePreviewControlsMobile
                toggleFullscreenAction={toggleFullscreenAction}
                fullscreen={fullscreen}
                onCopy={onCopy}
                toggleDrawingBar={toggleDrawingBar}
                renderFullscreenControls={() => (
                  <DetailviewFullscreenControls
                    gotoNextSibling={gotoNextSibling}
                    gotoPrevSibling={gotoPrevSibling}
                    assetTitle={display_name}
                    siblings={siblings}
                  />
                )}
              /> :
              <ImagePreviewControls
                mainContainerRef={mainContainerRef.current}
                toggleFullscreenAction={toggleFullscreenAction}
                fullscreen={fullscreen}
                onCopy={onCopy}
                renderFullscreenControls={() => (
                  <DetailviewFullscreenControls
                    gotoNextSibling={gotoNextSibling}
                    gotoPrevSibling={gotoPrevSibling}
                    assetTitle={display_name}
                    siblings={siblings}
                  />
                )}
              />
            }
          </div>
        )}
      </PanZoomProvider>
    </div>
  );
};

export default ImagePreview;
